// SIMPLIFIED Stripe Connect Button - No Client ID needed!
// Uses controller-based accounts with your existing STRIPE_SECRET_KEY
// This does NOT affect your subscription billing system

import React, { useState, useEffect } from 'react';
import { CreditCard, Check, ExternalLink, AlertCircle, Loader2, RefreshCw, Unlink } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabaseClient';

interface StripeConnectButtonProps {
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

export default function StripeConnectButton({ size = 'medium', className = '' }: StripeConnectButtonProps) {
  const { selectedBusiness: business, refreshBusinesses } = useBusiness();
  const [loading, setLoading] = useState(false);
  const [showDisconnectOption, setShowDisconnectOption] = useState(false);
  // Simplified status tracking
  const [connectStatus, setConnectStatus] = useState<{
    isConnected: boolean;
    accountId?: string;
    onboardingCompleted?: boolean;
    chargesEnabled?: boolean;
  }>({ isConnected: false });

  // Check current connection status
  useEffect(() => {
    if (business) {
      setConnectStatus({
        isConnected: !!business.stripe_connect_account_id,
        accountId: business.stripe_connect_account_id,
        onboardingCompleted: business.stripe_connect_onboarding_completed,
        chargesEnabled: business.stripe_connect_charges_enabled,
      });
    }
  }, [business]);

  // Refresh business data when URL parameters suggest Stripe Connect return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('stripe_connect') === 'success') {
      // Wait a moment for the status check to complete, then refresh
      setTimeout(() => {
        refreshBusinesses();
      }, 2000);
    }
  }, [refreshBusinesses]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showDisconnectOption) {
        setShowDisconnectOption(false);
      }
    };

    if (showDisconnectOption) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDisconnectOption]);

  const handleConnectStripe = async () => {
    if (!business) return;
    
    setLoading(true);
    try {
      console.log('🔗 Starting SIMPLIFIED Stripe Connect flow (no Client ID needed)...');
      
      // Call Supabase Edge Function to create controller-based Connect account
      const { data, error } = await supabase.functions.invoke('create-stripe-connect-account', {
        body: {
          businessId: business.id,
          businessEmail: business.email || '',
          businessName: business.name || '',
          returnUrl: `${window.location.origin}/settings/integrations?stripe_connect=success`,
          refreshUrl: `${window.location.origin}/settings/integrations?stripe_connect=refresh`,
        }
      });

      if (error) {
        console.error('❌ Failed to create Connect account:', error);
        throw error;
      }

      console.log('✅ Controller-based Connect account created, redirecting to onboarding...');
      
      // Redirect to Stripe onboarding
      window.location.href = data.onboardingUrl;
      
    } catch (error) {
      console.error('❌ Error connecting to Stripe:', error);
      alert('Failed to connect to Stripe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshStatus = async () => {
    if (!business || !connectStatus.isConnected) {
      alert('No Stripe account connected to check status for.');
      return;
    }
    
    setLoading(true);
    try {
      console.log('🔄 Manually checking Stripe Connect status...');
      
      const { data, error } = await supabase.functions.invoke('check-stripe-connect-status', {
        body: { businessId: business.id }
      });

      if (error) {
        console.error('❌ Failed to check Stripe status:', error);
        throw error;
      }

      console.log('✅ Stripe status checked:', data);
      
      // Refresh business data to get the latest status
      await refreshBusinesses();
      
      // Show a success message
      alert('Stripe account status refreshed successfully!');
      
    } catch (error) {
      console.error('❌ Error checking Stripe status:', error);
      alert('Failed to check Stripe status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnectStripe = async () => {
    if (!business || !connectStatus.isConnected) return;
    
    if (!confirm('Are you sure you want to disconnect your Stripe account? This will disable payment processing.')) {
      return;
    }
    
    setShowDisconnectOption(false); // Close dropdown
    setLoading(true);
    try {
      console.log('🔌 Disconnecting Stripe Connect account...');
      
      // For now, just clear the database record (you can add actual disconnection later)
      const { error } = await supabase
        .from('businesses')
        .update({
          stripe_connect_account_id: null,
          stripe_connect_onboarding_completed: false,
          stripe_connect_charges_enabled: false,
        })
        .eq('id', business.id);

      if (error) {
        console.error('❌ Failed to disconnect Connect account:', error);
        throw error;
      }

      console.log('✅ Stripe account disconnected successfully');
      
      // Refresh business data
      await refreshBusinesses();
      
      alert('Stripe account disconnected successfully!');
      
    } catch (error) {
      console.error('❌ Error disconnecting from Stripe:', error);
      alert('Failed to disconnect from Stripe. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Size classes
  const sizeClasses = {
    small: 'px-3 py-1.5 text-xs',
    medium: 'px-4 py-2 text-sm',
    large: 'px-6 py-3 text-base'
  };

  const iconSizes = {
    small: 'h-3 w-3',
    medium: 'h-4 w-4',
    large: 'h-5 w-5'
  };

  if (!business) {
    return null;
  }

  // Connected and fully set up
  if (connectStatus.isConnected && connectStatus.onboardingCompleted && connectStatus.chargesEnabled) {
    return (
      <div className={`relative ${className}`}>
        <button
          onClick={() => setShowDisconnectOption(!showDisconnectOption)}
          className={`flex items-center justify-center space-x-2 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 transition-colors ${sizeClasses[size]} w-full`}
        >
          <Check className={iconSizes[size]} />
          <span className="font-medium">Connected</span>
          <ExternalLink className={iconSizes[size]} />
        </button>
        
        {showDisconnectOption && (
          <div 
            className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => window.open(`https://dashboard.stripe.com/connect/accounts/${connectStatus.accountId}`, '_blank')}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-t-lg flex items-center space-x-2"
            >
              <ExternalLink className="h-4 w-4" />
              <span>Open Stripe Dashboard</span>
            </button>
            <button
              onClick={handleDisconnectStripe}
              disabled={loading}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 rounded-b-lg flex items-center space-x-2"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Unlink className="h-4 w-4" />
              )}
              <span>Disconnect Stripe</span>
            </button>
          </div>
        )}
      </div>
    );
  }

  // Connected but onboarding incomplete
  if (connectStatus.isConnected && !connectStatus.onboardingCompleted) {
    return (
      <div className={`flex flex-col space-y-2 ${className}`}>
        <button
          onClick={handleConnectStripe}
          disabled={loading}
          className={`flex items-center justify-center space-x-2 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors ${sizeClasses[size]} w-full`}
        >
          {loading ? (
            <Loader2 className={`${iconSizes[size]} animate-spin`} />
          ) : (
            <AlertCircle className={iconSizes[size]} />
          )}
          <span className="font-medium">Complete Stripe Setup</span>
          <ExternalLink className={iconSizes[size]} />
        </button>
        
        <button
          onClick={handleDisconnectStripe}
          disabled={loading}
          className={`flex items-center justify-center space-x-2 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 transition-colors ${sizeClasses[size]} w-full`}
        >
          {loading ? (
            <Loader2 className={`${iconSizes[size]} animate-spin`} />
          ) : (
            <Unlink className={iconSizes[size]} />
          )}
          <span className="font-medium">Disconnect Stripe</span>
        </button>
      </div>
    );
  }

  // Not connected
  return (
    <button
      onClick={handleConnectStripe}
      disabled={loading}
      className={`flex items-center space-x-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors ${sizeClasses[size]} ${className}`}
    >
      {loading ? (
        <Loader2 className={`${iconSizes[size]} animate-spin`} />
      ) : (
        <CreditCard className={iconSizes[size]} />
      )}
      <span className="font-medium">Connect Stripe</span>
      <ExternalLink className={iconSizes[size]} />
    </button>
  );
}
